import { useEffect, useState, type FormEvent } from "react";

const navLinks = [
  { href: "#archive", label: "证据汇总" },
  { href: "#timeline", label: "时间线核对" },
  { href: "#source", label: "来源说明" },
  { href: "#faq", label: "常见问题" },
];

type TopNavProps = {
  onSearch: (query: string) => void;
};

export default function TopNav({ onSearch }: TopNavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [value, setValue] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearch(value.trim());
    setMenuOpen(false);
    document.getElementById("archive")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="flex h-16 w-full items-center gap-4 px-4 md:h-[72px] md:px-6">
        <a href="#top" className="flex shrink-0 items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-xl font-bold text-background-50">
            黑
          </span>
          <span className="flex flex-col leading-none">
            <h1 className="font-heading text-lg font-black tracking-wide text-foreground-950 md:text-xl">
              黑料照片
            </h1>
            <span className="mt-1 font-label text-[10px] uppercase tracking-[0.22em] text-foreground-500">
              Evidence Archive
            </span>
          </span>
        </a>

        <nav className="ml-6 hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <form
          onSubmit={handleSubmit}
          className="ml-auto hidden items-center gap-2 md:flex"
        >
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100/70 px-3 py-2">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder="按事件名检索…"
              className="w-40 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-52"
            />
          </div>
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-full bg-foreground-950 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            进入检索
          </button>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <form onSubmit={handleSubmit} className="mb-3 flex items-center gap-2 rounded-full border border-background-300 bg-background-100/70 px-3 py-2">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder="按事件名检索…"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </form>
          <div className="flex flex-col">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="cursor-pointer whitespace-nowrap rounded-md px-2 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}